import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Alert, Box, Typography, Button } from "@mui/material";
import { Hourglass } from "lucide-react";
import { motion } from "framer-motion";
import { useAssessmentStore } from "../../stores/assessmentStore";
import {
  AssessmentQuestion,
  getAssessmentHistoryDetail,
  type AssessmentAnswerItem,
} from "../../axios/quiz";
import { AssessmentQuizContent } from "./assessment-quiz-content/assessment-quiz-content";
import { AssessmentWelcome } from "./assessment-welcome/assessment-welcome";
import { QuizFinish } from "./quiz-finish/quiz-finish";
import {
  AssessmentContestResults,
  mapHistoryQuestionsToFeedbackItems,
  type AssessmentQuestionFeedbackItem,
} from "./assessment-contest-results/assessment-contest-results";
import { useStatisticsStore } from "../../stores/statisticsStore";
import { useAppStore } from "../../stores/appStore";
import { checkSessionStatus } from "../../axios/user";
import { useAuthStore } from "../../stores/authStore";
import { useUserStore } from "../../stores/userStore";
import {
  findNextUnsubmittedQuestionIndex,
  findPreviousUnsubmittedQuestionIndex,
  getCurrentUserLeaderboardBonusScore,
  getCurrentUserLeaderboardScore,
  resolveAssessmentGamificationEnabled,
} from "./assessment-contest-utils";
import { AssessmentManagerDashboard } from "./assessment-manager-dashboard/assessment-manager-dashboard";
import {
  assessmentOverlayCardStyle,
  assessmentPopUpOuterStyle,
  assessmentPopUpShellStyle,
} from "./assessment-layout-styles";
import { useServerAnchoredTimeLeft } from "../../hooks/useServerAnchoredTimeLeft";
import type { AssessmentInteractiveSubmitGetterRef } from "../interactive-questions/assessment-mode";
import {
  assessmentQuestionToAnswerBlock,
  getAssessmentQuestionMaxScore,
  getAssessmentQuestionType,
  isInteractiveAssessmentType,
  isMultipleChoiceAnswers,
  isOpenTextAssessmentType,
  resolveAssessmentQuestionPreviewContent,
  resolveAssessmentQuestionPreviewText,
  serializeAssessmentInteractiveAnswer,
  type AssessmentSendAnswerOptions,
  type QuestionPreviewSegment,
} from "../../utils/assessmentInteractive";
import {
  getAssessmentElapsedSeconds,
  normalizeAssessmentTotalTimeSpent,
} from "../../utils/assessmentTimeLeft";
import { isAxiosNetworkError } from "../../utils/internetConnection";
import {
  isCompleteOpenTextAnswer,
  OPEN_TEXT_INCOMPLETE_MESSAGE,
} from "../../utils/openTextAnswerValidation";

const OFFLINE_ANSWER_MESSAGE =
  "Could not save your answer. Check your connection and try again.";
const SUBMIT_ALL_ERROR_MESSAGE =
  "Could not submit your answers. Check your connection and try again.";
const MISSING_REQUIRED_ANSWERS_MESSAGE =
  "Some answers are missing. Answer every question, including bonus questions, then finish again.";
const FINISH_CONFLICT_MESSAGE =
  "Finish is blocked until every required answer is saved, including bonus questions.";
const RESULTS_LOAD_ERROR_MESSAGE =
  "Could not load challenge results. Check your connection and try again.";
const HISTORY_FEEDBACK_RETRY_ATTEMPTS = 3;
const HISTORY_FEEDBACK_RETRY_DELAY_MS = 800;

const waitMs = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

type HistoryFeedbackFetchResult = {
  ok: boolean;
  networkError?: boolean;
};

type PendingAssessmentAnswer = {
  answer: AssessmentAnswerItem;
  options?: AssessmentSendAnswerOptions;
  question: AssessmentQuestion;
  questionIndex: number;
  resolvedChosenAnswer: string;
  isInteractiveQuestion: boolean;
  isOpenText: boolean;
};

interface AssessmentPopUpProps {
  open: boolean;
  onClose: () => void;
}

export const AssessmentPopUp = ({ open, onClose }: AssessmentPopUpProps) => {
  const {
    assessmentData,
    assessmentStats,
    sendAnswer,
    fetchAssessmentStat,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    clearStore,
    error,
    statsError,
    clearStatsError,
    currentAnswerResponse,
    setError,
    draftAnswers,
    upsertDraftAnswer,
    getDraftAnswer,
    finishAssessmentSession,
    startAssessmentSession,
    syncAssessmentAttempt,
    questionStartTime,
  } = useAssessmentStore();
  const { setRefreshedLeaderBoard, setRefreshedProgressBar } =
    useStatisticsStore();
  const {
    focus,
    setIsAssessmentOpen,
    setDeviceConflictModalOpen,
    deviceConflictModalOpen,
  } = useAppStore();
  const authUserId = useAuthStore((state) => state.userId);
  const storedUserId = useUserStore((state) => state.user.id);
  const currentUserId = storedUserId || authUserId || null;
  const isManager = useUserStore((state) => Boolean(state.user?.is_manager));
  const [isAnswerChecked, setIsAnswerChecked] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState<string | null>(null);
  const [isFinish, setIsFinish] = useState(false);
  const startTimeRef = useRef<number | null>(null);
  const baselineTimeSpentRef = useRef(0);
  const [timeSpent, setTimeSpent] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editSubmitKind, setEditSubmitKind] = useState<
    "next" | "submit" | null
  >(null);
  const [answerSaveError, setAnswerSaveError] = useState<string | null>(null);
  const [connectionRestored, setConnectionRestored] = useState(false);
  const [isNetworkOnline, setIsNetworkOnline] = useState<boolean>(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [submittingDisplayAnswer, setSubmittingDisplayAnswer] = useState<
    string | null
  >(null);
  const [interactiveComplete, setInteractiveComplete] = useState(false);
  const [interactiveCheckedState, setInteractiveCheckedState] = useState<{
    userAnswers?: Record<string, string>;
    userOrder?: string[];
    userPairs?: Array<{ left_id: string; right_id: string }>;
    isCorrect?: boolean;
    feedback?: string;
  } | null>(null);
  const [historyQuestionFeedbacks, setHistoryQuestionFeedbacks] = useState<
    AssessmentQuestionFeedbackItem[]
  >([]);
  const [answerResetKey, setAnswerResetKey] = useState(0);
  const [openTextFocusRequestKey, setOpenTextFocusRequestKey] = useState(0);
  // After Previous/Next restore, open_text stays read-only until Retry.
  const [openTextLocked, setOpenTextLocked] = useState(false);
  const [isAnswerCommitted, setIsAnswerCommitted] = useState(false);
  const interactiveSubmitGetterRef =
    useRef<AssessmentInteractiveSubmitGetterRef["current"]>(null);
  const pendingAnswerRef = useRef<PendingAssessmentAnswer | null>(null);
  const [hasStartedAssessment, setHasStartedAssessment] = useState(false);
  const [showContestResults, setShowContestResults] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsLoadError, setResultsLoadError] = useState<string | null>(null);
  const [finishSource, setFinishSource] = useState<"manual" | "timer" | null>(
    null,
  );
  const [managerDashboardOpen, setManagerDashboardOpen] = useState(false);
  const [managerDashboardType, setManagerDashboardType] = useState<
    "overview" | "questions"
  >("questions");
  const conflictCheckDoneRef = useRef<boolean>(false);
  const timerExpiredRef = useRef<boolean>(false);
  const failedSubmitIndicesRef = useRef<number[] | null>(null);
  const resumeAttemptSyncIdRef = useRef<string | null>(null);
  const visibilitySyncInFlightRef = useRef(false);
  const wasOfflineRef = useRef<boolean>(
    typeof navigator === "undefined" ? false : !navigator.onLine,
  );

  const managerQuestionPreviewByIndex = useMemo(() => {
    const map: Record<number, string> = {};
    assessmentData?.questions?.forEach((question, index) => {
      const text = resolveAssessmentQuestionPreviewText(question);
      if (text.trim()) {
        map[index] = text.trim();
      }
    });
    return map;
  }, [assessmentData?.questions]);

  const managerQuestionPreviewSegmentsByIndex = useMemo(() => {
    const map: Record<number, QuestionPreviewSegment[]> = {};
    assessmentData?.questions?.forEach((question, index) => {
      const segments = resolveAssessmentQuestionPreviewContent(question);
      if (segments.length) {
        map[index] = segments;
      }
    });
    return map;
  }, [assessmentData?.questions]);

  const managerDashboardCurrentQuestionLabel = `Q${Math.max(
    1,
    currentQuestionIndex + 1,
  )}`;

  const calculateElapsedSeconds = useCallback(() => {
    return getAssessmentElapsedSeconds(
      startTimeRef.current,
      baselineTimeSpentRef.current,
      timeSpent,
    );
  }, [timeSpent]);

  const beginTimedSession = useCallback(
    (serverTotalTimeSpent?: number | null) => {
      baselineTimeSpentRef.current =
        normalizeAssessmentTotalTimeSpent(serverTotalTimeSpent);
      startTimeRef.current = Date.now();
    },
    [],
  );

  const handleFinish = useCallback(() => {
    const seconds = calculateElapsedSeconds();

    if (seconds > 0) {
      setTimeSpent(seconds);
    }
    startTimeRef.current = null;

    setRefreshedLeaderBoard(true);
    setRefreshedProgressBar(true);
    setFinishSource("timer");
    setIsFinish(true);
  }, [
    calculateElapsedSeconds,
    setRefreshedLeaderBoard,
    setRefreshedProgressBar,
  ]);

  const handleTimerFinish = useCallback(async () => {
    try {
      const storeState = useAssessmentStore.getState();
      // Bank current soft draft locally only — do not POST after personal deadline.
      if (storeState.assessmentData?.answer_editing_enabled === true) {
        const pending = pendingAnswerRef.current;
        if (pending) {
          const start = storeState.questionStartTime;
          const timeSpent = start
            ? Math.floor((Date.now() - start) / 1000)
            : 0;
          storeState.upsertDraftAnswer({
            answer: pending.answer,
            options: pending.options,
            timeSpent:
              (storeState.draftAnswers[pending.questionIndex]?.timeSpent ?? 0) +
              timeSpent,
            finalized:
              storeState.draftAnswers[pending.questionIndex]?.finalized ===
              true,
          });
        }
      }

      // Timeout path: GET /attempt (server sweep finalizes persisted answers).
      let synced = await storeState.syncAssessmentAttempt({
        reconcileAnswers: false,
      });
      for (
        let attempt = 0;
        attempt < 3 &&
        synced.ok &&
        synced.status?.state === "active" &&
        (synced.status.time_left ?? 0) <= 0;
        attempt += 1
      ) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        synced = await storeState.syncAssessmentAttempt({
          reconcileAnswers: false,
        });
      }

      if (!synced.ok) {
        console.error(
          "Error syncing assessment attempt on timer expiry:",
          synced.message,
        );
      }
    } catch (error) {
      console.error("Error finishing assessment on timer expiry:", error);
    }
    handleFinish();
  }, [handleFinish]);

  const completeQuizManually = useCallback(() => {
    const seconds = calculateElapsedSeconds();

    if (seconds > 0) {
      setTimeSpent(seconds);
    }
    startTimeRef.current = null;

    setRefreshedLeaderBoard(true);
    setRefreshedProgressBar(true);
    setFinishSource("manual");
    setIsFinish(true);
  }, [
    calculateElapsedSeconds,
    setRefreshedLeaderBoard,
    setRefreshedProgressBar,
  ]);

  useEffect(() => {
    if (open) {
      setIsFinish(false);
      setTimeSpent(0);
      startTimeRef.current = null;
      baselineTimeSpentRef.current = 0;
      setIsAnswerChecked(false);
      setCurrentAnswer(null);
      setAnswerSaveError(null);
      setSubmittingDisplayAnswer(null);
      setEditSubmitKind(null);
      setHasStartedAssessment(false);
      setShowContestResults(false);
      setFinishSource(null);
      setHistoryQuestionFeedbacks([]);
      pendingAnswerRef.current = null;
      timerExpiredRef.current = false;
      failedSubmitIndicesRef.current = null;
      resumeAttemptSyncIdRef.current = null;
      setManagerDashboardOpen(false);
      setManagerDashboardType("questions");
    } else if (!open) {
      setIsFinish(false);
      setTimeSpent(0);
      setHasStartedAssessment(false);
      setShowContestResults(false);
      setFinishSource(null);
      setHistoryQuestionFeedbacks([]);
      timerExpiredRef.current = false;
      resumeAttemptSyncIdRef.current = null;
      setManagerDashboardOpen(false);
      setManagerDashboardType("questions");
      startTimeRef.current = null;
      baselineTimeSpentRef.current = 0;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !assessmentData?.assessment_id) {
      return;
    }

    const assessmentId = assessmentData.assessment_id;
    const serverAnswered = assessmentData.answered_questions_number ?? 0;
    const editingEnabled = assessmentData.answer_editing_enabled === true;
    const localDraftCount = Object.keys(
      useAssessmentStore.getState().draftAnswers,
    ).length;
    const answered = editingEnabled
      ? Math.max(serverAnswered, localDraftCount)
      : serverAnswered;

    if (answered <= 0) {
      return;
    }

    const totalQuestionsCount =
      assessmentData.total_questions_number ??
      assessmentData.questions?.length ??
      0;
    const alreadyCompleted =
      totalQuestionsCount > 0 && serverAnswered >= totalQuestionsCount;

    if (alreadyCompleted) {
      // Use server total only — do not start a live session clock on reopen.
      baselineTimeSpentRef.current = normalizeAssessmentTotalTimeSpent(
        assessmentData.total_time_spent,
      );

      setHasStartedAssessment(true);
      if (!editingEnabled || !hasStartedAssessment) {
        setIsFinish(true);
        setFinishSource((prev) => prev ?? "manual");
      }
      return;
    }

    // Sync /attempt once per assessment open — avoid loops when sync rewrites drafts.
    if (resumeAttemptSyncIdRef.current === assessmentId) {
      if (!hasStartedAssessment) {
        setHasStartedAssessment(true);
      }
      return;
    }
    resumeAttemptSyncIdRef.current = assessmentId;

    let cancelled = false;
    void (async () => {
      // Reload / device handoff: reconcile from GET /attempt, not welcome clock.
      const synced = await syncAssessmentAttempt({ reconcileAnswers: true });
      if (cancelled) return;

      if (
        synced.status?.state === "finished" ||
        synced.status?.state === "expired"
      ) {
        setHasStartedAssessment(true);
        setIsFinish(true);
        setFinishSource((prev) => prev ?? "timer");
        return;
      }

      if (startTimeRef.current == null) {
        beginTimedSession(assessmentData.total_time_spent);
      }
      setHasStartedAssessment(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    assessmentData?.assessment_id,
    assessmentData?.answered_questions_number,
    assessmentData?.answer_editing_enabled,
    assessmentData?.total_questions_number,
    assessmentData?.questions?.length,
    assessmentData?.total_time_spent,
    beginTimedSession,
    hasStartedAssessment,
    syncAssessmentAttempt,
  ]);

  useEffect(() => {
    if (assessmentData?.answer_editing_enabled === true) {
      return;
    }
    setIsAnswerChecked(false);
    setCurrentAnswer(null);
    setAnswerSaveError(null);
    setConnectionRestored(false);
    setSubmittingDisplayAnswer(null);
    setInteractiveComplete(false);
    setInteractiveCheckedState(null);
    interactiveSubmitGetterRef.current = null;
    pendingAnswerRef.current = null;
    conflictCheckDoneRef.current = false;
    setAnswerResetKey(0);
    setIsAnswerCommitted(false);
  }, [currentQuestionIndex, assessmentData?.answer_editing_enabled]);

  // After reload/resume in edit mode, hydrate the current question from drafts once.
  useEffect(() => {
    if (
      !open ||
      !hasStartedAssessment ||
      isFinish ||
      assessmentData?.answer_editing_enabled !== true
    ) {
      return;
    }

    const questionIndex = useAssessmentStore.getState().currentQuestionIndex;
    const draft = getDraftAnswer(questionIndex);
    const question = assessmentData?.questions?.[questionIndex];
    if (!draft || !question) {
      return;
    }

    const questionType = getAssessmentQuestionType(question);
    const isInteractive = isInteractiveAssessmentType(questionType);
    const isOpenText = isOpenTextAssessmentType(questionType);
    const interactivePayload = draft.options?.interactivePayload;

    pendingAnswerRef.current = {
      answer: draft.answer,
      options: draft.options,
      question,
      questionIndex,
      resolvedChosenAnswer: draft.answer.chosen_answer,
      isInteractiveQuestion: isInteractive,
      isOpenText,
    };

    setCurrentAnswer(draft.answer.chosen_answer);
    setIsAnswerChecked(false);
    setIsAnswerCommitted(draft.finalized === true);
    setOpenTextLocked(isOpenText || draft.finalized === true);
    setAnswerSaveError(null);
    conflictCheckDoneRef.current = false;
    useAssessmentStore.setState({ currentAnswerResponse: null });

    if (interactivePayload) {
      const checkedState: {
        userAnswers?: Record<string, string>;
        userOrder?: string[];
        userPairs?: Array<{ left_id: string; right_id: string }>;
      } = {};
      if ("user_answers" in interactivePayload) {
        checkedState.userAnswers = interactivePayload.user_answers;
      }
      if ("user_order" in interactivePayload) {
        checkedState.userOrder = interactivePayload.user_order;
      }
      if ("user_pairs" in interactivePayload) {
        checkedState.userPairs = interactivePayload.user_pairs;
      }
      setInteractiveCheckedState(checkedState);
      setInteractiveComplete(true);
    } else {
      setInteractiveCheckedState(null);
      setInteractiveComplete(false);
    }
  }, [
    open,
    hasStartedAssessment,
    isFinish,
    assessmentData?.answer_editing_enabled,
    assessmentData?.assessment_id,
    getDraftAnswer,
    assessmentData?.questions,
  ]);

  useEffect(() => {
    const syncNetworkStatus = () => {
      setIsNetworkOnline(navigator.onLine);
    };

    const handleOnline = () => setIsNetworkOnline(true);
    const handleOffline = () => setIsNetworkOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const pollId = window.setInterval(syncNetworkStatus, 1500);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.clearInterval(pollId);
    };
  }, []);

  // Reconcile personal deadline after tab sleep / device handoff.
  useEffect(() => {
    if (!open || !hasStartedAssessment || isFinish) {
      return;
    }

    const reconcile = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      if (visibilitySyncInFlightRef.current || timerExpiredRef.current) {
        return;
      }
      visibilitySyncInFlightRef.current = true;
      void syncAssessmentAttempt({ reconcileAnswers: false })
        .then((synced) => {
          if (
            synced.status?.state === "finished" ||
            synced.status?.state === "expired" ||
            (typeof synced.status?.time_left === "number" &&
              synced.status.time_left <= 0)
          ) {
            void handleTimerFinish();
          }
        })
        .finally(() => {
          visibilitySyncInFlightRef.current = false;
        });
    };

    document.addEventListener("visibilitychange", reconcile);
    window.addEventListener("focus", reconcile);
    return () => {
      document.removeEventListener("visibilitychange", reconcile);
      window.removeEventListener("focus", reconcile);
    };
  }, [
    open,
    hasStartedAssessment,
    isFinish,
    syncAssessmentAttempt,
    handleTimerFinish,
  ]);

  useEffect(() => {
    if (!isNetworkOnline) {
      wasOfflineRef.current = true;
      return;
    }

    if (!wasOfflineRef.current) {
      return;
    }

    wasOfflineRef.current = false;
    setAnswerSaveError(null);
    setConnectionRestored(true);

    const hideTimeout = setTimeout(() => {
      setConnectionRestored(false);
    }, 10000);

    return () => clearTimeout(hideTimeout);
  }, [isNetworkOnline]);

  useEffect(() => {
    const assessmentId = assessmentData?.assessment_id ?? null;
    setHistoryQuestionFeedbacks([]);
    if (!assessmentId) {
      return;
    }
  }, [assessmentData?.assessment_id]);

  const fetchHistoryQuestionFeedbacks =
    useCallback(async (): Promise<HistoryFeedbackFetchResult> => {
      const assessmentId = assessmentData?.assessment_id;
      if (!assessmentId) {
        return { ok: true };
      }

      if (!navigator.onLine) {
        return { ok: false, networkError: true };
      }

      // Timeout / contest finalization can lag slightly behind the finish
      // screen — retry before treating feedback as unavailable.
      let sawNetworkError = false;
      for (
        let attempt = 0;
        attempt < HISTORY_FEEDBACK_RETRY_ATTEMPTS;
        attempt += 1
      ) {
        try {
          const detail = await getAssessmentHistoryDetail(assessmentId);
          setHistoryQuestionFeedbacks(
            mapHistoryQuestionsToFeedbackItems(detail.questions ?? []),
          );
          return { ok: true };
        } catch (historyError) {
          const networkError =
            !navigator.onLine || isAxiosNetworkError(historyError);
          sawNetworkError = sawNetworkError || networkError;
          console.error(
            "Error fetching assessment history question feedbacks:",
            historyError,
          );
          // Fail fast on offline — retries won't help until connection returns.
          if (networkError && !navigator.onLine) {
            return { ok: false, networkError: true };
          }
          if (attempt < HISTORY_FEEDBACK_RETRY_ATTEMPTS - 1) {
            await waitMs(HISTORY_FEEDBACK_RETRY_DELAY_MS * (attempt + 1));
          }
        }
      }

      return { ok: false, networkError: sawNetworkError };
    }, [assessmentData?.assessment_id]);

  const handleViewChallengeResults = useCallback(async () => {
    if (resultsLoading) {
      return;
    }

    setResultsLoading(true);
    setResultsLoadError(null);

    try {
      if (!isNetworkOnline || !navigator.onLine) {
        setResultsLoadError(RESULTS_LOAD_ERROR_MESSAGE);
        return;
      }

      // Retry stats after idle/sleep — polls + abort races are common on wake.
      let statsOk = false;
      for (
        let attempt = 0;
        attempt < HISTORY_FEEDBACK_RETRY_ATTEMPTS;
        attempt += 1
      ) {
        statsOk = await fetchAssessmentStat();
        if (statsOk) {
          break;
        }
        const { assessmentStats, statsError } =
          useAssessmentStore.getState();
        if (!statsError && assessmentStats) {
          statsOk = true;
          break;
        }
        if (attempt < HISTORY_FEEDBACK_RETRY_ATTEMPTS - 1) {
          await waitMs(HISTORY_FEEDBACK_RETRY_DELAY_MS * (attempt + 1));
        }
      }

      if (!navigator.onLine || !isNetworkOnline) {
        setResultsLoadError(RESULTS_LOAD_ERROR_MESSAGE);
        return;
      }

      // Soft-open results after timeout/idle wake: finish screen already has
      // scores; a flaky /assessment_stat must not block challenge results.
      clearStatsError();
      if (!statsOk) {
        console.warn(
          "Opening challenge results without a fresh /assessment_stat response",
        );
      }

      const historyResult = await fetchHistoryQuestionFeedbacks();
      if (!historyResult.ok) {
        // Offline / network failure: stay on finish with Retry.
        if (
          historyResult.networkError ||
          !navigator.onLine ||
          !isNetworkOnline
        ) {
          setResultsLoadError(RESULTS_LOAD_ERROR_MESSAGE);
          return;
        }
        setShowContestResults(true);
        setResultsLoadError(null);
        return;
      }

      setShowContestResults(true);
      setResultsLoadError(null);
    } catch (viewResultsError) {
      console.error(
        "Error fetching assessment stats before results:",
        viewResultsError,
      );
      // Still open when online — same soft-fail as a flaky stats poll.
      if (navigator.onLine && isNetworkOnline) {
        clearStatsError();
        setShowContestResults(true);
        setResultsLoadError(null);
      } else {
        setResultsLoadError(RESULTS_LOAD_ERROR_MESSAGE);
      }
    } finally {
      setResultsLoading(false);
    }
  }, [
    clearStatsError,
    fetchAssessmentStat,
    fetchHistoryQuestionFeedbacks,
    isNetworkOnline,
    resultsLoading,
  ]);

  const handleClose = async () => {
    try {
      const sessionStatus = await checkSessionStatus();
      if (sessionStatus.status === "conflict") {
        setDeviceConflictModalOpen(true);
      }
    } catch (error) {
      console.error("Error checking session status on close:", error);
      // Continue with close if check fails
    }

    setIsFinish(false);
    setTimeSpent(0);
    setHasStartedAssessment(false);
    setShowContestResults(false);
    setResultsLoading(false);
    setResultsLoadError(null);
    setFinishSource(null);
    setHistoryQuestionFeedbacks([]);
    startTimeRef.current = null;
    baselineTimeSpentRef.current = 0;
    clearStore();
    onClose();
  };

  const handleStartChallenge = useCallback(async () => {
    if (!assessmentData) return;

    setAnswerSaveError(null);
    const started = await startAssessmentSession();
    if (!started.ok) {
      setAnswerSaveError(
        started.message ??
          "Could not start the contest timer. Check your connection and try again.",
      );
      return;
    }

    if (
      started.status?.state === "finished" ||
      started.status?.state === "expired"
    ) {
      setHasStartedAssessment(true);
      setFinishSource("timer");
      setIsFinish(true);
      return;
    }

    beginTimedSession(assessmentData.total_time_spent);
    timerExpiredRef.current = false;

    const serverAnswered = assessmentData.answered_questions_number ?? 0;
    const editingEnabled = assessmentData.answer_editing_enabled === true;
    const resumeIndex = editingEnabled
      ? useAssessmentStore.getState().currentQuestionIndex
      : serverAnswered;

    setCurrentQuestionIndex(resumeIndex);
    setHasStartedAssessment(true);
  }, [
    assessmentData,
    beginTimedSession,
    setCurrentQuestionIndex,
    startAssessmentSession,
  ]);

  // Show waiting indicator if there's a conflict error and device conflict modal is not yet open
  const isWaitingForConflictModal =
    error &&
    error.includes("close it on your other device") &&
    !deviceConflictModalOpen;

  // Periodically check for device conflict modal while waiting
  useEffect(() => {
    if (!isWaitingForConflictModal) return;

    const checkInterval = setInterval(async () => {
      try {
        const sessionStatus = await checkSessionStatus();
        if (sessionStatus.status === "conflict") {
          setDeviceConflictModalOpen(true);
        }
      } catch (error) {
        // Ignore errors
      }
    }, 2000);
    return () => clearInterval(checkInterval);
  }, [
    isWaitingForConflictModal,
    setDeviceConflictModalOpen,
    error,
    deviceConflictModalOpen,
  ]);

  useEffect(() => {
    if (!open || !assessmentData?.assessment_id || isWaitingForConflictModal) {
      return;
    }

    if (!isManager && !hasStartedAssessment && !isFinish) {
      return;
    }

    let isCancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const scheduleNextFetch = () => {
      if (isCancelled) {
        return;
      }

      const randomDelayMs = 5000 + Math.floor(Math.random() * 5001);

      timeoutId = setTimeout(async () => {
        try {
          await fetchAssessmentStat();
        } catch (pollError) {
          console.error("Error polling assessment stats:", pollError);
        } finally {
          scheduleNextFetch();
        }
      }, randomDelayMs);
    };

    void fetchAssessmentStat();
    scheduleNextFetch();

    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    open,
    assessmentData?.assessment_id,
    fetchAssessmentStat,
    isWaitingForConflictModal,
    isManager,
    hasStartedAssessment,
    isFinish,
  ]);

  const totalQuestions =
    assessmentData?.total_questions_number ??
    assessmentData?.questions?.length ??
    0;
  const answerEditingEnabled = assessmentData?.answer_editing_enabled === true;
  const draftAnswerCount = Object.keys(draftAnswers).length;
  const answeredQuestions = answerEditingEnabled
    ? Math.max(assessmentData?.answered_questions_number ?? 0, draftAnswerCount)
    : (assessmentData?.answered_questions_number ?? 0);

  // Prev/Next in edit mode prefer questions that still need Submit & Next.
  const previousUnsubmittedQuestionIndex = useMemo(() => {
    if (!answerEditingEnabled || totalQuestions <= 0) {
      return null;
    }
    return findPreviousUnsubmittedQuestionIndex(
      totalQuestions,
      draftAnswers,
      currentQuestionIndex,
    );
  }, [
    answerEditingEnabled,
    totalQuestions,
    draftAnswers,
    currentQuestionIndex,
  ]);

  const isAssessmentCompleted =
    totalQuestions > 0 &&
    (assessmentData?.answered_questions_number ?? 0) >= totalQuestions;
  const userFinishedAssessment = answerEditingEnabled
    ? isFinish
    : isFinish || isAssessmentCompleted;
  const isContestFinished = assessmentStats?.assessment_finished === true;

  // Freeze "Time spent" when the user finishes — stop the live clock and keep
  // the final elapsed value for the finish screen.
  useEffect(() => {
    if (!userFinishedAssessment || startTimeRef.current == null) {
      return;
    }

    const seconds = getAssessmentElapsedSeconds(
      startTimeRef.current,
      baselineTimeSpentRef.current,
    );
    if (seconds > 0) {
      setTimeSpent(seconds);
    }
    startTimeRef.current = null;
  }, [userFinishedAssessment]);

  const resolvedLeaderboard = useMemo(() => {
    const statLeaderboard = assessmentStats?.leaderboard ?? [];
    if (statLeaderboard.length > 0) {
      return statLeaderboard;
    }
    return assessmentData?.leaderboard ?? [];
  }, [assessmentData?.leaderboard, assessmentStats?.leaderboard]);
  const leaderboardScore = getCurrentUserLeaderboardScore(
    resolvedLeaderboard,
    currentUserId,
  );
  const leaderboardBonusScore = getCurrentUserLeaderboardBonusScore(
    resolvedLeaderboard,
    currentUserId,
  );
  const personalScore =
    assessmentData?.user_score ?? assessmentData?.scores_earned;
  const personalBonusScore =
    assessmentData?.gamification_bonus_score ?? assessmentData?.bonus_score;
  const showFeedbackMode = assessmentData?.show_feedback ?? "after_answer";
  // In edit + after_completion hide earned points until Finish. With after_answer
  // show live scores after each submit so feedback matches the leaderboard.
  const hideDeferredScores =
    answerEditingEnabled &&
    !userFinishedAssessment &&
    showFeedbackMode !== "after_answer";
  const currentUserScore = hideDeferredScores
    ? 0
    : typeof personalScore === "number"
      ? personalScore
      : leaderboardScore;
  const currentUserBonusScore = hideDeferredScores
    ? 0
    : typeof personalBonusScore === "number"
      ? personalBonusScore
      : leaderboardBonusScore;
  const currentMandatoryScore = hideDeferredScores
    ? 0
    : (assessmentData?.mandatory_score ?? 0);
  const currentMandatoryMaxScore = assessmentData?.mandatory_max_score ?? 0;
  const currentBonusQuestionScore = hideDeferredScores
    ? 0
    : (assessmentData?.bonus_question_score ?? 0);
  const currentBonusQuestionMaxScore =
    assessmentData?.bonus_question_max_score ?? 0;
  const scoreBreakdown = {
    mandatoryScore: currentMandatoryScore,
    mandatoryMaxScore: currentMandatoryMaxScore,
    bonusQuestionScore: currentBonusQuestionScore,
    bonusQuestionMaxScore: currentBonusQuestionMaxScore,
    userScore: currentUserScore,
    gamificationBonusScore: currentUserBonusScore,
  };
  const timerTotalSeconds = Math.max(1, assessmentData?.time_limit ?? 0);
  const gamificationEnabled = resolveAssessmentGamificationEnabled(
    assessmentData,
  );
  // End results always include per-question Q&A, independent of live feedback mode
  // (after_answer / after_completion / not_show).
  const showEndQuestionFeedbacks = true;
  // Manual Finish after all questions, or global contest ended (timer path waits for assessment_finished).
  // Question feedback lives on contest results, so completed attempts always get that screen.
  const canViewChallengeResults =
    isContestFinished ||
    (isFinish && isAssessmentCompleted && finishSource === "manual") ||
    (isFinish && showEndQuestionFeedbacks);

  // Finish screen: never keep a stats poll error. Only show Retry when offline.
  useEffect(() => {
    if (!open || !(isFinish || userFinishedAssessment)) {
      return;
    }

    clearStatsError();

    if (showContestResults || !canViewChallengeResults) {
      return;
    }

    if (!isNetworkOnline) {
      setResultsLoadError(RESULTS_LOAD_ERROR_MESSAGE);
      return;
    }

    setResultsLoadError(null);
  }, [
    open,
    showContestResults,
    canViewChallengeResults,
    isFinish,
    userFinishedAssessment,
    isNetworkOnline,
    clearStatsError,
  ]);

  useEffect(() => {
    if (
      !open ||
      !showEndQuestionFeedbacks ||
      !assessmentData?.assessment_id ||
      !(isFinish || userFinishedAssessment || showContestResults)
    ) {
      return;
    }

    if (historyQuestionFeedbacks.length > 0) {
      return;
    }

    let cancelled = false;

    void fetchHistoryQuestionFeedbacks().then((result) => {
      if (!cancelled && result.ok) {
        setResultsLoadError(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    showEndQuestionFeedbacks,
    assessmentData?.assessment_id,
    isFinish,
    userFinishedAssessment,
    showContestResults,
    isContestFinished,
    historyQuestionFeedbacks.length,
    fetchHistoryQuestionFeedbacks,
  ]);
  const resolvedTimeSpent = userFinishedAssessment
    ? timeSpent > 0
      ? timeSpent
      : normalizeAssessmentTotalTimeSpent(assessmentData?.total_time_spent)
    : timeSpent > 0
      ? timeSpent
      : calculateElapsedSeconds();

  const anchoredRemainingSeconds = useServerAnchoredTimeLeft(
    assessmentData?.time_left,
    {
      assessmentId: assessmentData?.assessment_id,
      isPaused: !open || !hasStartedAssessment || isContestFinished,
    },
  );
  const remainingSeconds =
    isContestFinished || finishSource === "timer"
      ? 0
      : anchoredRemainingSeconds;

  useEffect(() => {
    if (!open || !assessmentData?.assessment_id || !userFinishedAssessment) {
      return;
    }

    // Background refresh only — failures must not surface a finish-screen banner.
    void fetchAssessmentStat().finally(() => {
      clearStatsError();
      setResultsLoadError((prev) =>
        prev && prev !== RESULTS_LOAD_ERROR_MESSAGE ? null : prev,
      );
    });
  }, [
    open,
    assessmentData?.assessment_id,
    userFinishedAssessment,
    fetchAssessmentStat,
    clearStatsError,
  ]);

  useEffect(() => {
    if (
      !hasStartedAssessment ||
      timerExpiredRef.current ||
      remainingSeconds > 0 ||
      isFinish
    ) {
      return;
    }

    timerExpiredRef.current = true;
    void handleTimerFinish();
  }, [hasStartedAssessment, remainingSeconds, isFinish, handleTimerFinish]);

  useEffect(() => {
    if (
      !hasStartedAssessment ||
      !isContestFinished ||
      isFinish ||
      timerExpiredRef.current
    ) {
      return;
    }

    timerExpiredRef.current = true;
    void handleTimerFinish();
  }, [hasStartedAssessment, isContestFinished, isFinish, handleTimerFinish]);

  if (!open) {
    return null;
  }

  if (isWaitingForConflictModal) {
    return (
      <Box sx={assessmentPopUpOuterStyle}>
        <Box
          sx={{
            ...assessmentOverlayCardStyle,
            background: "#FFFFFF",
            zIndex: 100,
            gap: 3,
            alignItems: "center",
          }}
        >
          <motion.div
            animate={{
              rotate: [0, 180, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Hourglass size={48} color="#1976d2" strokeWidth={2} />
          </motion.div>
          <Typography
            variant="h6"
            sx={{ textAlign: "center", color: "#000000" }}
          >
            Activating assessment on this device
          </Typography>
          <Typography
            variant="body2"
            sx={{ textAlign: "center", color: "#000000" }}
          >
            Please wait, this may take a few seconds
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error && !isWaitingForConflictModal) {
    return (
      <Box sx={assessmentPopUpOuterStyle}>
        <Box
          sx={{
            ...assessmentOverlayCardStyle,
            background: "#FFFFFF",
            zIndex: 100,
            gap: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{ textAlign: "center", color: "error.main" }}
          >
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              setError(null);
              clearStore();
              setIsAssessmentOpen(false);
            }}
            sx={{
              alignSelf: "center",
              minWidth: "120px",
              color: "#ffffff",
            }}
          >
            OK
          </Button>
        </Box>
      </Box>
    );
  }

  if (
    !assessmentData ||
    !assessmentData.questions ||
    assessmentData.questions.length === 0
  ) {
    if (isFinish || isAssessmentCompleted) {
      const totalMaxScore =
        assessmentData?.questions?.reduce(
          (acc, q) => acc + getAssessmentQuestionMaxScore(q),
          0,
        ) ?? 0;

      const calculatedTotalMaxScore =
        assessmentData?.questions &&
        assessmentData.questions.length > 0 &&
        assessmentData.questions.length < totalQuestions
          ? Math.round(
              (totalMaxScore / assessmentData.questions.length) *
                totalQuestions,
            )
          : totalMaxScore;
      const fullMark =
        assessmentData?.mandatory_max_score ?? calculatedTotalMaxScore;

      return (
        <QuizFinish
          totalQuestions={totalQuestions}
          currentQuestionIndex={answeredQuestions}
          onClose={handleClose}
          onContinue={
            canViewChallengeResults ? handleViewChallengeResults : handleClose
          }
          continueLabel={
            canViewChallengeResults
              ? "View challenge results"
              : "Continue conversation"
          }
          continueLoading={canViewChallengeResults ? resultsLoading : false}
          continueDisabled={canViewChallengeResults ? resultsLoading : false}
          resultsError={canViewChallengeResults ? resultsLoadError : null}
          onRetryResults={
            canViewChallengeResults ? handleViewChallengeResults : undefined
          }
          timeSpent={resolvedTimeSpent}
          scoresEarned={currentUserScore}
          totalMaxScore={fullMark}
          bonusScore={currentUserBonusScore}
          scoreBreakdown={scoreBreakdown}
          hideTimeSpent={false}
          fullScreenOnMobile
          isContest
          contestTitle={assessmentData?.title ?? null}
          assessmentData={{
            scoresEarned: currentUserScore,
            totalMaxScore: fullMark,
            bonusScore: currentUserBonusScore,
            focus: focus || "",
          }}
        />
      );
    }
    return (
      <Box sx={assessmentPopUpOuterStyle}>
        <Box
          sx={{
            ...assessmentOverlayCardStyle,
            background: "#FFFFFF",
            zIndex: 100,
            justifyContent: "center",
          }}
        >
          <Typography variant="h6">Loading questions...</Typography>
        </Box>
      </Box>
    );
  }

  if (currentQuestionIndex >= assessmentData.questions.length) {
    if (!userFinishedAssessment) {
      return (
        <Box sx={assessmentPopUpOuterStyle}>
          <Box
            sx={{
              ...assessmentOverlayCardStyle,
              background: "#FFFFFF",
              zIndex: 100,
              justifyContent: "center",
            }}
          >
            <Typography variant="h6">Loading next question...</Typography>
          </Box>
        </Box>
      );
    }
  }

  const currentQuestion: AssessmentQuestion =
    assessmentData.questions[
      Math.min(
        Math.max(0, currentQuestionIndex),
        Math.max(0, assessmentData.questions.length - 1),
      )
    ];

  const handleAnswerSelect = (answer: string) => {
    if (isSubmitting) return;
    if (answerEditingEnabled && isAnswerCommitted) {
      const draft = getDraftAnswer(currentQuestionIndex);
      if (draft?.finalized) {
        return;
      }
    }

    // Update the controlled input immediately.
    setCurrentAnswer(answer);
    setAnswerSaveError(null);
    if (answerEditingEnabled) {
      pendingAnswerRef.current = null;
      setIsAnswerChecked(false);
      setIsAnswerCommitted(false);
    } else if (isAnswerChecked) {
      setIsAnswerChecked(false);
      setInteractiveCheckedState(null);
      pendingAnswerRef.current = null;
    }

    // Check for device conflict once per question; do not block typing.
    if (!conflictCheckDoneRef.current && answer && answer.trim().length > 0) {
      conflictCheckDoneRef.current = true;
      void (async () => {
        try {
          const sessionStatus = await checkSessionStatus();
          if (sessionStatus.status === "conflict") {
            setError(
              "To continue the contest, please close it on your other device first.",
            );
            setIsAssessmentOpen(true);
          }
        } catch (error) {
          console.error(
            "Error checking session status on answer select:",
            error,
          );
        }
      })();
    }
  };

  const handleRetryAnswer = () => {
    if (isSubmitting || !answerEditingEnabled) {
      return;
    }
    const draft = getDraftAnswer(currentQuestionIndex);
    if (draft?.finalized) {
      // "Submit & Next" answers stay locked until the assessment ends.
      return;
    }
    const hasSelection = Boolean(
      (currentAnswer && currentAnswer.trim()) || interactiveComplete,
    );
    if (!hasSelection && !isAnswerChecked && !isAnswerCommitted) {
      return;
    }
    const preserveOpenTextAnswer = isOpenTextAssessmentType(
      getAssessmentQuestionType(currentQuestion),
    );
    setIsAnswerChecked(false);
    setIsAnswerCommitted(false);
    if (!preserveOpenTextAnswer) {
      setCurrentAnswer(null);
      setInteractiveComplete(false);
      setInteractiveCheckedState(null);
      interactiveSubmitGetterRef.current = null;
      setAnswerResetKey((key) => key + 1);
      setOpenTextLocked(false);
    } else {
      // Unlock + focus so the learner can edit the preserved text.
      setOpenTextLocked(false);
      setOpenTextFocusRequestKey((key) => key + 1);
    }
    setSubmittingDisplayAnswer(null);
    setAnswerSaveError(null);
    pendingAnswerRef.current = null;
    useAssessmentStore.setState({
      questionStartTime: Date.now(),
      currentAnswerResponse: null,
    });
  };

  const getCurrentQuestionTimeSpent = () => {
    const start =
      questionStartTime ?? useAssessmentStore.getState().questionStartTime;
    return start ? Math.floor((Date.now() - start) / 1000) : 0;
  };

 // Bank the current viewing seconds into the local draft without POSTing.
  const accumulateCurrentViewTimeIntoDraft = (
    pending: PendingAssessmentAnswer,
    opts?: { finalized?: boolean },
  ) => {
    const existing = getDraftAnswer(pending.questionIndex);
    const segment = getCurrentQuestionTimeSpent();
    const accumulated = Math.max(0, (existing?.timeSpent ?? 0) + segment);
    upsertDraftAnswer({
      answer: pending.answer,
      options: pending.options,
      timeSpent: accumulated,
      finalized: opts?.finalized ?? existing?.finalized ?? false,
    });
    useAssessmentStore.setState({ questionStartTime: Date.now() });
  };

  const savePendingAsDraft = (
    pending: PendingAssessmentAnswer,
    opts?: { finalized?: boolean; timeSpent?: number },
  ) => {
    const existing = getDraftAnswer(pending.questionIndex);
    upsertDraftAnswer({
      answer: pending.answer,
      options: pending.options,
      timeSpent:
        typeof opts?.timeSpent === "number"
          ? opts.timeSpent
          : (existing?.timeSpent ?? 0),
      finalized: opts?.finalized ?? existing?.finalized ?? false,
    });
  };

  const sendEditModeAnswer = async (
    pending: PendingAssessmentAnswer,
    opts?: { finalized?: boolean; action?: "next" | "submit" },
  ): Promise<boolean> => {
    if (!navigator.onLine) {
      setAnswerSaveError(OFFLINE_ANSWER_MESSAGE);
      return false;
    }

    const existing = getDraftAnswer(pending.questionIndex);
    // Include locally banked viewing time + current open segment.
    const timeSpent =
      Math.max(0, existing?.timeSpent ?? 0) + getCurrentQuestionTimeSpent();
    setEditSubmitKind(opts?.action ?? (opts?.finalized ? "submit" : "next"));
    setIsSubmitting(true);
    setAnswerSaveError(null);
    setSubmittingDisplayAnswer(pending.resolvedChosenAnswer);

    const revealAfterAnswer = showFeedbackMode === "after_answer";

    try {
      const saved = await sendAnswer(pending.answer, {
        ...pending.options,
        timeSpent,
        // Keep score/justification/answers when after_answer feedback is enabled.
        suppressFeedback: !revealAfterAnswer,
        isSubmitted: opts?.finalized === true,
      });
      if (!saved) {
        setAnswerSaveError(OFFLINE_ANSWER_MESSAGE);
        return false;
      }

      // Counter reset after a successful POST — backend owns the sum.
      savePendingAsDraft(pending, {
        finalized: opts?.finalized,
        timeSpent: 0,
      });
      pendingAnswerRef.current = pending;
      setIsAnswerChecked(revealAfterAnswer);
      setIsAnswerCommitted(Boolean(opts?.finalized));
      if (opts?.finalized || pending.isOpenText) {
        setOpenTextLocked(true);
      }
      if (revealAfterAnswer && pending.isInteractiveQuestion) {
        const interactivePayload = pending.options?.interactivePayload;
        const checkedState: NonNullable<typeof interactiveCheckedState> = {};
        if (interactivePayload && "user_answers" in interactivePayload) {
          checkedState.userAnswers = interactivePayload.user_answers;
        }
        if (interactivePayload && "user_order" in interactivePayload) {
          checkedState.userOrder = interactivePayload.user_order;
        }
        if (interactivePayload && "user_pairs" in interactivePayload) {
          checkedState.userPairs = interactivePayload.user_pairs;
        }
        setInteractiveCheckedState(checkedState);
        setInteractiveComplete(true);
      }
      useAssessmentStore.setState({
        ...(revealAfterAnswer ? {} : { currentAnswerResponse: null }),
        questionStartTime: Date.now(),
      });
      return true;
    } catch (error) {
      console.error("Error sending edit-mode answer:", error);
      setAnswerSaveError(OFFLINE_ANSWER_MESSAGE);
      return false;
    } finally {
      setIsSubmitting(false);
      setEditSubmitKind(null);
      setSubmittingDisplayAnswer(null);
    }
  };

  const resetQuestionUiState = () => {
    setIsAnswerChecked(false);
    setIsAnswerCommitted(false);
    setOpenTextLocked(false);
    setCurrentAnswer(null);
    setInteractiveComplete(false);
    setInteractiveCheckedState(null);
    interactiveSubmitGetterRef.current = null;
    pendingAnswerRef.current = null;
    setAnswerSaveError(null);
    setSubmittingDisplayAnswer(null);
    conflictCheckDoneRef.current = false;
  };

  const restoreDraftForIndex = (index: number) => {
    const draft = getDraftAnswer(index);
    const question = assessmentData?.questions?.[index];
    if (!draft || !question) {
      resetQuestionUiState();
      useAssessmentStore.setState({ currentAnswerResponse: null });
      return;
    }

    const questionType = getAssessmentQuestionType(question);
    const isInteractive = isInteractiveAssessmentType(questionType);
    const isOpenText = isOpenTextAssessmentType(questionType);
    const interactivePayload = draft.options?.interactivePayload;

    pendingAnswerRef.current = {
      answer: draft.answer,
      options: draft.options,
      question,
      questionIndex: index,
      resolvedChosenAnswer: draft.answer.chosen_answer,
      isInteractiveQuestion: isInteractive,
      isOpenText,
    };

    setCurrentAnswer(draft.answer.chosen_answer);
    // Restore selection only — do not mark checked or surface score feedback
    // while the learner can still edit answers.
    const isFinalized = draft.finalized === true;
    setIsAnswerChecked(false);
    setIsAnswerCommitted(isFinalized);
    setOpenTextLocked(isOpenText || isFinalized);
    setAnswerSaveError(null);
    setSubmittingDisplayAnswer(null);
    conflictCheckDoneRef.current = false;
    useAssessmentStore.setState({ currentAnswerResponse: null });

    if (interactivePayload) {
      const checkedState: NonNullable<typeof interactiveCheckedState> = {};
      if ("user_answers" in interactivePayload) {
        checkedState.userAnswers = interactivePayload.user_answers;
      }
      if ("user_order" in interactivePayload) {
        checkedState.userOrder = interactivePayload.user_order;
      }
      if ("user_pairs" in interactivePayload) {
        checkedState.userPairs = interactivePayload.user_pairs;
      }
      setInteractiveCheckedState(checkedState);
      setInteractiveComplete(true);
    } else {
      setInteractiveCheckedState(null);
      setInteractiveComplete(false);
    }
  };

  const submitFinalAnswer = async (
    pending: PendingAssessmentAnswer,
  ): Promise<boolean> => {
    if (!navigator.onLine) {
      setAnswerSaveError(OFFLINE_ANSWER_MESSAGE);
      return false;
    }

    setIsSubmitting(true);
    setAnswerSaveError(null);
    setSubmittingDisplayAnswer(pending.resolvedChosenAnswer);

    try {
      const saved = await sendAnswer(pending.answer, pending.options);
      if (!saved) {
        setAnswerSaveError(OFFLINE_ANSWER_MESSAGE);
        return false;
      }

      pendingAnswerRef.current = null;
      setIsAnswerCommitted(true);
      return true;
    } catch (error) {
      console.error("Error sending answer:", error);
      setAnswerSaveError(OFFLINE_ANSWER_MESSAGE);
      return false;
    } finally {
      setIsSubmitting(false);
      setSubmittingDisplayAnswer(null);
    }
  };

  const buildPendingAnswer = (options?: {
    allowIncompleteOpenText?: boolean;
  }): PendingAssessmentAnswer | null => {
    if (!assessmentData) return null;

    const questionForSubmit = assessmentData.questions[currentQuestionIndex];
    if (!questionForSubmit) return null;

    const questionType = getAssessmentQuestionType(questionForSubmit);
    const isInteractiveQuestion = isInteractiveAssessmentType(questionType);
    const isOpenText = isOpenTextAssessmentType(questionType);
    const selectedConcept = questionForSubmit.competency;
    const questionIndex = currentQuestionIndex;

    if (!isInteractiveQuestion && (!currentAnswer || !currentAnswer.trim())) {
      return null;
    }

    const lockedAnswer =
      questionType === "open_text"
        ? (currentAnswer ?? "").trim()
        : (currentAnswer ?? "");

    if (isInteractiveQuestion) {
      const submitData = interactiveSubmitGetterRef.current?.();
      if (!submitData) {
        setAnswerSaveError(
          "Complete the question before checking your answer.",
        );
        return null;
      }

      const chosenAnswer = serializeAssessmentInteractiveAnswer(
        submitData.payload,
        questionForSubmit,
      );
      const totalScore = getAssessmentQuestionMaxScore(questionForSubmit);
      const checkedState: NonNullable<typeof interactiveCheckedState> = {};
      if ("user_answers" in submitData.payload) {
        checkedState.userAnswers = submitData.payload.user_answers;
      }
      if ("user_order" in submitData.payload) {
        checkedState.userOrder = submitData.payload.user_order;
      }
      if ("user_pairs" in submitData.payload) {
        checkedState.userPairs = submitData.payload.user_pairs;
      }
      setInteractiveCheckedState(checkedState);
      setCurrentAnswer(chosenAnswer);

      return {
        answer: {
          question_index: questionIndex,
          chosen_answer: chosenAnswer,
          concept: selectedConcept,
          score: null,
          all_score: totalScore,
        },
        options: {
          interactivePayload: submitData.payload,
          questionBlock: assessmentQuestionToAnswerBlock(
            questionForSubmit,
            questionIndex,
            chosenAnswer,
          ),
        },
        question: questionForSubmit,
        questionIndex,
        resolvedChosenAnswer: chosenAnswer,
        isInteractiveQuestion: true,
        isOpenText: false,
      };
    }

    if (isOpenText) {
      if (
        !options?.allowIncompleteOpenText &&
        !isCompleteOpenTextAnswer(lockedAnswer)
      ) {
        setAnswerSaveError(OPEN_TEXT_INCOMPLETE_MESSAGE);
        return null;
      }

      return {
        answer: {
          question_index: questionIndex,
          chosen_answer: lockedAnswer,
          concept: selectedConcept,
          score: null,
          all_score: 5,
        },
        question: questionForSubmit,
        questionIndex,
        resolvedChosenAnswer: lockedAnswer,
        isInteractiveQuestion: false,
        isOpenText: true,
      };
    }

    if (isMultipleChoiceAnswers(questionForSubmit.answers)) {
      const mcAnswers = questionForSubmit.answers;
      const selectedAnswerObj = mcAnswers.find(
        (ans) => ans.text === lockedAnswer,
      );
      if (!selectedAnswerObj) {
        setAnswerSaveError(
          "Invalid answer selection. Please pick an option again.",
        );
        return null;
      }

      const hasClientPoints = mcAnswers.every(
        (ans) => typeof ans.points === "number" && Number.isFinite(ans.points),
      );
      const totalScore = hasClientPoints
        ? mcAnswers.reduce((acc, ans) => acc + ans.points, 0)
        : (questionForSubmit.max_score ?? 0);
      return {
        answer: {
          question_index: questionIndex,
          chosen_answer: lockedAnswer,
          concept: selectedConcept,
          score: hasClientPoints ? selectedAnswerObj.points : null,
          all_score: totalScore,
        },
        question: questionForSubmit,
        questionIndex,
        resolvedChosenAnswer: lockedAnswer,
        isInteractiveQuestion: false,
        isOpenText: false,
      };
    }

    setAnswerSaveError(
      "Invalid answer selection. Please pick an option again.",
    );
    return null;
  };

  const openTextSubmitFallbackError = () => {
    if (
      isOpenTextAssessmentType(getAssessmentQuestionType(currentQuestion)) &&
      currentAnswer?.trim() &&
      !isCompleteOpenTextAnswer(currentAnswer)
    ) {
      return OPEN_TEXT_INCOMPLETE_MESSAGE;
    }
    return null;
  };

  const handleCheckAnswer = async () => {
    if (isSubmitting || isAnswerCommitted) return;
    if (!assessmentData) return;

    setAnswerSaveError(null);
    const pending = buildPendingAnswer();
    if (!pending) return;

    // Edit mode no longer uses a local-only Check step — answers are sent on
    // Next / Submit & Next. Keep this path for non-edit assessments.
    if (answerEditingEnabled) {
      pendingAnswerRef.current = pending;
      useAssessmentStore.setState({ currentAnswerResponse: null });
      setIsAnswerChecked(false);
      return;
    }

    if (!navigator.onLine) {
      setAnswerSaveError(OFFLINE_ANSWER_MESSAGE);
      return;
    }

    const saved = await submitFinalAnswer(pending);
    if (saved) {
      setIsAnswerChecked(true);
    }
  };

  const handleConfirmAnswer = async () => {
    if (isSubmitting || isAnswerCommitted || !answerEditingEnabled) return;

    const pending = pendingAnswerRef.current ?? buildPendingAnswer();
    if (!pending) {
      setAnswerSaveError("Select an answer before continuing.");
      return;
    }

    await sendEditModeAnswer(pending, { finalized: false });
  };

  const navigateToQuestion = (nextIndex: number) => {
    resetQuestionUiState();
    setCurrentQuestionIndex(nextIndex);
    if (answerEditingEnabled) {
      restoreDraftForIndex(nextIndex);
    }
  };

  const isDraftAlreadySynced = (
    pending: PendingAssessmentAnswer,
    existing: ReturnType<typeof getDraftAnswer>,
  ) =>
    existing != null &&
    existing.answer.chosen_answer === pending.answer.chosen_answer;

  // Advance only through questions that still need Submit & Next. When every
  // answer is locked, land on the last question so Finish can complete.
  const navigateToNextUnsubmittedOrFinish = async (opts?: {
    treatCurrentAsFinalized?: boolean;
  }): Promise<boolean> => {
    const drafts = useAssessmentStore.getState().draftAnswers;
    const treatAsFinalized =
      opts?.treatCurrentAsFinalized === true
        ? new Set([currentQuestionIndex])
        : undefined;
    const nextIndex = findNextUnsubmittedQuestionIndex(
      totalQuestions,
      drafts,
      currentQuestionIndex,
      treatAsFinalized,
    );

    if (nextIndex != null) {
      navigateToQuestion(nextIndex);
      return false;
    }

    if (currentQuestionIndex < totalQuestions - 1) {
      navigateToQuestion(totalQuestions - 1);
      return false;
    }

    const submitted = await submitDeferredAnswers();
    if (!submitted) {
      return false;
    }
    completeQuizManually();
    return true;
  };

  const handlePreviousQuestion = async () => {
    if (!answerEditingEnabled || isSubmitting) {
      return;
    }

    const targetIndex = findPreviousUnsubmittedQuestionIndex(
      totalQuestions,
      useAssessmentStore.getState().draftAnswers,
      currentQuestionIndex,
    );
    if (targetIndex == null) {
      return;
    }

    try {
      const sessionStatus = await checkSessionStatus();
      if (sessionStatus.status === "conflict") {
        setError(
          "To continue the contest, please close it on your other device first.",
        );
        return;
      }
    } catch (error) {
      console.error(
        "Error checking session status before previous question:",
        error,
      );
    }

    const pending = buildPendingAnswer({ allowIncompleteOpenText: true });
    if (pending) {
      const existing = getDraftAnswer(currentQuestionIndex);
      if (existing?.finalized) {
        // Locked answers are not re-POSTed — bank view time only if we ever unlock.
        accumulateCurrentViewTimeIntoDraft(pending, { finalized: true });
      } else if (!isDraftAlreadySynced(pending, existing)) {
        const sent = await sendEditModeAnswer(pending, {
          finalized: false,
          action: "next",
        });
        if (!sent) return;
      } else {
        // Unchanged soft answer — no POST; keep viewing seconds for later send.
        accumulateCurrentViewTimeIntoDraft(pending, { finalized: false });
      }
    }

    navigateToQuestion(targetIndex);
  };

  const handleNextQuestion = async () => {
    try {
      const sessionStatus = await checkSessionStatus();
      if (sessionStatus.status === "conflict") {
        setError(
          "To continue the contest, please close it on your other device first.",
        );
        return;
      }
    } catch (error) {
      console.error(
        "Error checking session status before next question:",
        error,
      );
    }

    if (answerEditingEnabled) {
      const existing = getDraftAnswer(currentQuestionIndex);
      if (existing?.finalized) {
        await navigateToNextUnsubmittedOrFinish({
          treatCurrentAsFinalized: true,
        });
        return;
      }

      const pending = buildPendingAnswer();
      if (!pending) {
        setAnswerSaveError(
          openTextSubmitFallbackError() ??
            "Select an answer before going to the next question.",
        );
        return;
      }

      // Soft Next already posted this answer — skip re-send unless it changed.
      // Bank current viewing seconds locally until the next real POST.
      if (!isDraftAlreadySynced(pending, existing)) {
        const sent = await sendEditModeAnswer(pending, {
          finalized: false,
          action: "next",
        });
        if (!sent) return;
        if (showFeedbackMode === "after_answer") {
          return;
        }
      } else {
        accumulateCurrentViewTimeIntoDraft(pending, { finalized: false });
      }

      await navigateToNextUnsubmittedOrFinish();
      return;
    }

    if (!isAnswerCommitted) {
      setAnswerSaveError(
        "Check your answer before going to the next question.",
      );
      return;
    }

    navigateToQuestion(currentQuestionIndex + 1);
  };

  const handleSubmitAndNextQuestion = async () => {
    if (!answerEditingEnabled || isSubmitting) {
      return;
    }

    try {
      const sessionStatus = await checkSessionStatus();
      if (sessionStatus.status === "conflict") {
        setError(
          "To continue the contest, please close it on your other device first.",
        );
        return;
      }
    } catch (error) {
      console.error(
        "Error checking session status before submit & next:",
        error,
      );
    }

    const pending = buildPendingAnswer();
    if (!pending) {
      setAnswerSaveError(
        openTextSubmitFallbackError() ?? "Select an answer before submitting.",
      );
      return;
    }

    const existing = getDraftAnswer(currentQuestionIndex);
    const alreadySynced = isDraftAlreadySynced(pending, existing);

    // Already locked — no extra /assessment_answer.
    if (alreadySynced && existing?.finalized === true) {
      await navigateToNextUnsubmittedOrFinish({
        treatCurrentAsFinalized: true,
      });
      return;
    }

    // Soft draft with same text still needs is_submitted=true to lock.
    const sent = await sendEditModeAnswer(pending, {
      finalized: true,
      action: "submit",
    });
    if (!sent) return;

    if (showFeedbackMode === "after_answer") {
      return;
    }

    await navigateToNextUnsubmittedOrFinish({
      treatCurrentAsFinalized: true,
    });
  };

  const submitDeferredAnswers = async (): Promise<boolean> => {
    if (!navigator.onLine) {
      setAnswerSaveError(SUBMIT_ALL_ERROR_MESSAGE);
      return false;
    }

    // Save the current answer into drafts first (soft draft is enough).
    const pending = buildPendingAnswer({ allowIncompleteOpenText: true });
    if (pending) {
      const existing = getDraftAnswer(pending.questionIndex);
      accumulateCurrentViewTimeIntoDraft(pending, {
        finalized: existing?.finalized === true,
      });
    }

    const latestDrafts = useAssessmentStore.getState().draftAnswers;
    const missingRequiredIndices: number[] = [];
    for (let i = 0; i < totalQuestions; i += 1) {
      const draft = latestDrafts[i];
      const chosen = draft?.answer?.chosen_answer;
      if (typeof chosen !== "string" || !chosen.trim()) {
        missingRequiredIndices.push(i);
        continue;
      }
      const question = assessmentData?.questions?.[i];
      if (
        question &&
        isOpenTextAssessmentType(getAssessmentQuestionType(question)) &&
        !isCompleteOpenTextAnswer(chosen)
      ) {
        missingRequiredIndices.push(i);
      }
    }

    if (missingRequiredIndices.length > 0) {
      const hasIncompleteOpenText = missingRequiredIndices.some((index) => {
        const question = assessmentData?.questions?.[index];
        return (
          !!question &&
          isOpenTextAssessmentType(getAssessmentQuestionType(question)) &&
          !isCompleteOpenTextAnswer(
            latestDrafts[index]?.answer?.chosen_answer ?? "",
          )
        );
      });
      setAnswerSaveError(
        hasIncompleteOpenText
          ? OPEN_TEXT_INCOMPLETE_MESSAGE
          : MISSING_REQUIRED_ANSWERS_MESSAGE,
      );
      return false;
    }

    setEditSubmitKind("next");
    setIsSubmitting(true);
    setAnswerSaveError(null);

    try {
      const finished = await finishAssessmentSession();
      if (!finished.ok) {
        if (finished.conflict) {
          setAnswerSaveError(finished.message ?? FINISH_CONFLICT_MESSAGE);
          return false;
        }
        if (finished.failedIndices && finished.failedIndices.length > 0) {
          failedSubmitIndicesRef.current = finished.failedIndices;
        }
        setAnswerSaveError(finished.message ?? SUBMIT_ALL_ERROR_MESSAGE);
        return false;
      }

      failedSubmitIndicesRef.current = null;
      return true;
    } catch (error) {
      console.error("Error finalizing edit-mode assessment:", error);
      setAnswerSaveError(SUBMIT_ALL_ERROR_MESSAGE);
      return false;
    } finally {
      setIsSubmitting(false);
      setEditSubmitKind(null);
    }
  };

  const handleFinishQuiz = async () => {
    if (answerEditingEnabled) {
      const pending = buildPendingAnswer({ allowIncompleteOpenText: true });
      if (!pending && !getDraftAnswer(currentQuestionIndex)) {
        setAnswerSaveError(
          openTextSubmitFallbackError() ?? "Select an answer before finishing.",
        );
        return;
      }
      const submitted = await submitDeferredAnswers();
      if (!submitted) return;
      completeQuizManually();
      return;
    }

    if (!isAnswerCommitted) {
      setAnswerSaveError("Check your answer before finishing.");
      return;
    }

    completeQuizManually();
  };

  const displayedSelectedAnswer =
    isSubmitting && submittingDisplayAnswer !== null
      ? submittingDisplayAnswer
      : currentAnswer || "";

  const currentQuestionType = getAssessmentQuestionType(currentQuestion);
  const isCurrentInteractive = isInteractiveAssessmentType(currentQuestionType);

  const isAnswerSelected = isCurrentInteractive
    ? interactiveComplete
    : Boolean(displayedSelectedAnswer.trim());

  const convertedQuestion: any = {
    ...currentQuestion,
    competency: currentQuestion.competency || "",
    type: currentQuestionType,
    question_type: currentQuestionType,
  };

  const totalMaxScore = assessmentData.questions
    ? assessmentData.questions.reduce(
        (acc, q) => acc + getAssessmentQuestionMaxScore(q),
        0,
      )
    : 0;

  const calculatedTotalMaxScore =
    assessmentData.questions &&
    assessmentData.questions.length > 0 &&
    assessmentData.questions.length < totalQuestions
      ? Math.round(
          (totalMaxScore / assessmentData.questions.length) * totalQuestions,
        )
      : totalMaxScore;
  const fullMark =
    assessmentData?.mandatory_max_score ?? calculatedTotalMaxScore;

  const shouldShowWelcomeScreen =
    !isFinish && !isAssessmentCompleted && !hasStartedAssessment;

  const openTextValidationError =
    answerSaveError === OPEN_TEXT_INCOMPLETE_MESSAGE
      ? OPEN_TEXT_INCOMPLETE_MESSAGE
      : null;
  const connectionErrorMessage =
    !shouldShowWelcomeScreen &&
    (!isNetworkOnline || (answerSaveError && !openTextValidationError))
      ? (answerSaveError ?? OFFLINE_ANSWER_MESSAGE)
      : null;
  const managerActions = isManager ? (
    <Box
      sx={{
        display: "flex",
        gap: { xs: 0.75, sm: 1 },
        flexWrap: { xs: "nowrap", sm: "wrap" },
        flexShrink: 0,
      }}
    >
      <Button
        size="small"
        variant="outlined"
        sx={{
          borderRadius: "999px",
          whiteSpace: "nowrap",
          fontSize: { xs: "11px", sm: "13px" },
          px: { xs: 1.25, sm: 2 },
          color: "#0EA5E9",
          borderColor: "rgba(14,165,233,0.45)",
          "&:hover": {
            borderColor: "#0EA5E9",
            backgroundColor: "rgba(14,165,233,0.08)",
          },
        }}
        onClick={() => {
          setManagerDashboardType("questions");
          setManagerDashboardOpen(true);
        }}
      >
        Live dashboard 1
      </Button>
      <Button
        size="small"
        variant="outlined"
        sx={{
          borderRadius: "999px",
          whiteSpace: "nowrap",
          fontSize: { xs: "11px", sm: "13px" },
          px: { xs: 1.25, sm: 2 },
          color: "#8B5CF6",
          borderColor: "rgba(139,92,246,0.45)",
          "&:hover": {
            borderColor: "#8B5CF6",
            backgroundColor: "rgba(139,92,246,0.08)",
          },
        }}
        onClick={() => {
          setManagerDashboardType("overview");
          setManagerDashboardOpen(true);
        }}
      >
        Live dashboard 2
      </Button>
    </Box>
  ) : null;

  return (
    <Box sx={assessmentPopUpOuterStyle}>
      <Box sx={assessmentPopUpShellStyle}>
        {statsError &&
        !userFinishedAssessment &&
        !(shouldShowWelcomeScreen && !isManager) ? (
          <Alert
            severity="error"
            onClose={clearStatsError}
            sx={{ mt: 1.5, mb: 2, mx: 1 }}
          >
            {statsError}
          </Alert>
        ) : null}
        {!userFinishedAssessment ? (
          <>
            {connectionErrorMessage ? (
              <Typography
                variant="body1"
                sx={{
                  color: "error.main",
                  textAlign: "center",
                  fontWeight: 700,
                  fontSize: { xs: "15px", sm: "16px" },
                  mt: 1.5,
                  mb: 2,
                  px: 1,
                }}
              >
                {connectionErrorMessage}
              </Typography>
            ) : null}
            {connectionRestored ? (
              <Typography
                variant="body1"
                sx={{
                  color: "success.main",
                  textAlign: "center",
                  fontWeight: 700,
                  fontSize: { xs: "15px", sm: "16px" },
                  mt: 1.5,
                  mb: 2,
                  px: 1,
                }}
              >
                Connection restored. You can continue the contest.
              </Typography>
            ) : null}
            {shouldShowWelcomeScreen ? (
              <AssessmentWelcome
                totalQuestions={totalQuestions}
                timeLimitSeconds={assessmentData?.time_limit ?? 0}
                answeredQuestions={answeredQuestions}
                currentUserPlace={assessmentData?.current_user_place}
                leaderboard={resolvedLeaderboard}
                gamificationEnabled={gamificationEnabled}
                focusLabel={focus || assessmentData?.questions?.[0]?.competency}
                contestTitle={assessmentData?.title ?? null}
                onStart={handleStartChallenge}
                onClose={handleClose}
                managerActions={managerActions}
              />
            ) : (
              <AssessmentQuizContent
                currentQuestion={convertedQuestion}
                currentQuestionIndex={currentQuestionIndex}
                totalQuestions={totalQuestions}
                selectedAnswer={displayedSelectedAnswer}
                isAnswerChecked={isAnswerChecked}
                isAnswerSelected={isAnswerSelected}
                isSubmitting={isSubmitting}
                editSubmitKind={editSubmitKind}
                onAnswerSelect={handleAnswerSelect}
                onCheckAnswer={handleCheckAnswer}
                onRetryAnswer={handleRetryAnswer}
                onConfirmAnswer={handleConfirmAnswer}
                onSubmitAndNextQuestion={handleSubmitAndNextQuestion}
                answerResetKey={answerResetKey}
                openTextFocusRequestKey={openTextFocusRequestKey}
                openTextValidationError={openTextValidationError}
                openTextLocked={openTextLocked}
                onPreviousQuestion={handlePreviousQuestion}
                onNextQuestion={handleNextQuestion}
                onFinish={handleFinishQuiz}
                onClose={onClose}
                remainingSeconds={remainingSeconds}
                totalSeconds={timerTotalSeconds}
                totalMaxScore={fullMark}
                scoresEarned={currentUserScore}
                bonusScore={currentUserBonusScore}
                scoreBreakdown={scoreBreakdown}
                answeredQuestions={answeredQuestions}
                answerResponse={currentAnswerResponse}
                leaderboard={resolvedLeaderboard}
                currentUserPlace={assessmentData?.current_user_place}
                gamificationEnabled={gamificationEnabled}
                showFeedbackMode={showFeedbackMode}
                answerEditingEnabled={answerEditingEnabled}
                isAnswerCommitted={isAnswerCommitted}
                canNavigatePrevious={previousUnsubmittedQuestionIndex != null}
                assessmentId={assessmentData?.assessment_id ?? null}
                contestTitle={assessmentData?.title ?? null}
                managerActions={managerActions}
                interactiveSubmitGetterRef={interactiveSubmitGetterRef}
                onInteractiveCompletionChange={setInteractiveComplete}
                isInteractiveAnswerChecked={isAnswerChecked}
                interactiveCheckedState={interactiveCheckedState ?? undefined}
                isCheckAnswerBlocked={!isNetworkOnline}
              />
            )}
          </>
        ) : (
          <>
            {!showContestResults ? (
              <QuizFinish
                totalQuestions={totalQuestions}
                currentQuestionIndex={answeredQuestions}
                onClose={handleClose}
                onContinue={
                  canViewChallengeResults
                    ? handleViewChallengeResults
                    : handleClose
                }
                continueLabel={
                  canViewChallengeResults
                    ? "View challenge results"
                    : "Continue conversation"
                }
                continueLoading={
                  canViewChallengeResults ? resultsLoading : false
                }
                continueDisabled={
                  canViewChallengeResults ? resultsLoading : false
                }
                resultsError={canViewChallengeResults ? resultsLoadError : null}
                onRetryResults={
                  canViewChallengeResults
                    ? handleViewChallengeResults
                    : undefined
                }
                timeSpent={resolvedTimeSpent}
                scoresEarned={currentUserScore}
                totalMaxScore={fullMark}
                bonusScore={currentUserBonusScore}
                scoreBreakdown={scoreBreakdown}
                hideTimeSpent={false}
                fullScreenOnMobile
                isContest
                contestTitle={assessmentData?.title ?? null}
                assessmentData={{
                  scoresEarned: currentUserScore,
                  totalMaxScore: fullMark,
                  bonusScore: currentUserBonusScore,
                  focus:
                    focus || assessmentData?.questions[0]?.competency || "",
                }}
              />
            ) : (
              <AssessmentContestResults
                totalQuestions={totalQuestions}
                answeredQuestions={answeredQuestions}
                totalMaxScore={fullMark}
                remainingSeconds={remainingSeconds}
                totalSeconds={timerTotalSeconds}
                leaderboard={resolvedLeaderboard}
                currentUserPlace={assessmentData?.current_user_place}
                assessmentStats={assessmentStats}
                scoreBreakdown={scoreBreakdown}
                questionFeedbacks={historyQuestionFeedbacks}
                gamificationEnabled={gamificationEnabled}
                contestTitle={assessmentData?.title ?? null}
                onReviewPerformance={() => setShowContestResults(false)}
                onClose={handleClose}
                managerActions={managerActions}
              />
            )}
          </>
        )}
      </Box>
      <AssessmentManagerDashboard
        open={managerDashboardOpen}
        onClose={() => setManagerDashboardOpen(false)}
        assessmentId={assessmentData?.assessment_id ?? null}
        questionIndex={currentQuestionIndex}
        currentQuestionLabel={managerDashboardCurrentQuestionLabel}
        questionPreviewByIndex={managerQuestionPreviewByIndex}
        questionPreviewSegmentsByIndex={managerQuestionPreviewSegmentsByIndex}
        dashboardType={managerDashboardType}
        selectedFocus={focus || ""}
      />
    </Box>
  );
};

export default AssessmentPopUp;
